import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useButton, useCurrency, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import editIcon from '~Assets/icons/edit.svg';
import { useExpressCartActions } from '../../../Providers/ExpressCartProvider';
import { useExpressProductLibrary } from '../../../Providers/ExpressProductLibraryProvider';
import { CartLineEdit, EposMenuItem } from '../../../types';
import ItemImage from '../../../Components/ItemImage';
import QuantityStepper from '../../../Components/QuantityStepper';
import CartChildLine from './CartChildLine';
import { CartLine as CartLineShape } from './types';

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/** Same 3:2 crop as ItemCard's image, just scaled down to fit a row. */
const THUMB_WIDTH = 72;

/** Slide in when added. */
const LINE_IN_MS = 380;
/** Slide out and fold shut before the cart drops the line. */
const LINE_OUT_MS = 280;
/** How long the glow lingers after the slide in. */
const LINE_GLOW_MS = 480;

// ---------------------------------------------------------------------------
// The box
// ---------------------------------------------------------------------------

/**
 * One grouped line: thumbnail, title, price; add-ons and the stepper below.
 *
 * States, all classes so nothing renders for them:
 * - `is-new`      slides in from the left and glows (set once, at mount)
 * - `is-leaving`  slides out and folds shut before the cart drops it
 * - `is-dimmed`   another line is being edited (set by CartContents)
 * - `.is-locked`  on the list while any line is being edited
 */
const Block = styled.div`
  position: relative;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 16px;
  background: var(--colorBlueGray100);
  will-change: transform, opacity;
  transition: opacity 0.25s ease-out;

  /* ---- new: slide in + glow ring ---- */

  &.is-new {
    animation: eposCartLineIn ${LINE_IN_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: 0 0 0 2px var(--eposButton), 0 8px 24px rgba(0, 0, 0, 0.12);
    opacity: 0;
  }

  &.is-new::after {
    animation: eposCartLineGlow ${LINE_IN_MS + LINE_GLOW_MS}ms ease-out both;
  }

  /* ---- leaving: slide out + fold (height/padding/margin set inline by foldOut) ---- */

  &.is-leaving {
    overflow: hidden;
    pointer-events: none;
    animation: none;
    opacity: 0;
    transform: translate3d(-40px, 0, 0) scale(0.96);
    transition: height ${LINE_OUT_MS}ms ease-in-out, padding ${LINE_OUT_MS}ms ease-in-out,
      margin ${LINE_OUT_MS}ms ease-in-out, opacity ${LINE_OUT_MS * 0.7}ms ease-in,
      transform ${LINE_OUT_MS * 0.7}ms ease-in;
  }

  &.is-leaving::after {
    animation: none;
  }

  /* ---- editing: dim the others, lock the steppers ---- */

  /* !important outranks the is-new animation's fill mode without restarting it. */
  &.is-dimmed {
    opacity: 0.3 !important;
    pointer-events: none;
  }

  .is-locked & [data-testid^='epos-stepper-'] {
    pointer-events: none;
    opacity: 0.35;
  }

  /* ---- keyframes ---- */

  @keyframes eposCartLineIn {
    from {
      opacity: 0;
      transform: translate3d(-40px, 0, 0) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0) scale(1);
    }
  }

  @keyframes eposCartLineGlow {
    0% {
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

// ---------------------------------------------------------------------------
// Top row: thumbnail, text, price
// ---------------------------------------------------------------------------

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RowDescription = styled.div`
  font-size: 12px;
  line-height: 1.3;
  opacity: 0.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/** "Age-restricted (18+)..." - the cart is where the shopper sees this. */
const AgeNote = styled(RowDescription)`
  font-weight: 600;
  opacity: 0.7;
`;

/** Struck original above the price paid, discount name under. */
const RowPrice = styled.span`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  text-align: right;
  white-space: nowrap;
  color: var(--eposText);
`;

const PaidPrice = styled.span`
  font-size: 16px;
  font-weight: 800;
  line-height: 1.25;
`;

const OriginalPrice = styled.span`
  font-size: 12px;
  font-weight: 600;
  opacity: 0.45;
  text-decoration: line-through;
`;

const DiscountName = styled.span`
  max-width: 110px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--eposButton);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ---------------------------------------------------------------------------
// Below the row: add-ons, edit badge, stepper
// ---------------------------------------------------------------------------

/** Lines up with the text, past the thumbnail. */
const Below = styled.div`
  margin-left: ${THUMB_WIDTH + 12}px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
`;

/** Add-ons as a quiet list, separated from the stepper by a hairline. */
const Children = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--eposBorderColor);
`;

/** Edit on the left, stepper on the right. */
const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/** Pencil + "Ändra"/"Edit". Fills in the button color while this line is being edited. */
const EditBadge = styled.button`
  appearance: none;
  border: 1.5px solid var(--eposBorderColor);
  margin: 0;
  padding: 7px 14px 7px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: var(--eposSurface);
  color: var(--eposText);
  font-family: var(--eposFontFamily);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease-out, border-color 0.15s ease-out;

  &.is-touching {
    filter: brightness(0.94);
    border-color: var(--eposButton);
  }

  .is-editing & {
    border-color: var(--eposButton);
    background: var(--eposButton);
    color: var(--eposButtonText);
  }

  /* Icon paints through background-color. */
  .is-editing & .SVG-icon {
    background-color: var(--eposButtonText);
  }
`;

/**
 * Scrolls the list so the block is in view. Arithmetic on the list only -
 * scrollIntoView would also scroll every ancestor, and with the portrait
 * drawer parked under the screen that dragged the whole stage up.
 */
const scrollIntoList = (block: HTMLElement) => {
  const list = block.closest('[data-epos-cart-list]');
  if (!(list instanceof HTMLElement)) {
    return;
  }

  const blockRect = block.getBoundingClientRect();
  const listRect = list.getBoundingClientRect();

  if (blockRect.bottom > listRect.bottom) {
    list.scrollTo({
      top: list.scrollTop + blockRect.bottom - listRect.bottom,
      behavior: 'smooth',
    });
  } else if (blockRect.top < listRect.top) {
    list.scrollTo({ top: list.scrollTop + blockRect.top - listRect.top, behavior: 'smooth' });
  }
};

/** Locks the current height so the transition has a number to start from, then folds to nothing. */
const foldOut = (block: HTMLElement) => {
  block.style.height = `${block.offsetHeight}px`;
  block.classList.add('is-leaving');

  window.requestAnimationFrame(() => {
    block.style.height = '0px';
    block.style.paddingTop = '0px';
    block.style.paddingBottom = '0px';
    block.style.marginBottom = '0px';
  });
};

/** What ItemDetail needs to reopen prefilled with this line's picks. */
const toEdit = (line: CartLineShape, item: EposMenuItem): CartLineEdit => ({
  item,
  ids: line.ids,
  variationIds: line.variations.map((variation) => variation.id),
  removedVariationIds: line.removedVariations.map((variation) => variation.id),
  // child.quantity is the total across the group; editMenuItem wants it per parent.
  upsellPicks: Object.fromEntries(
    line.children.map((child) => [child.itemId, child.quantity / line.ids.length]),
  ),
  comment: line.comment,
});

interface Props {
  line: CartLineShape;
  /** True when none of the line's units were in the cart before - only then does it slide in. */
  appeared: boolean;
  /** "Ändra"/"Edit" tapped - reopen ItemDetail prefilled with this line's picks. */
  onEdit: (edit: CartLineEdit) => void;
}

/** Plus/minus talk to the cart actions directly - the list above owns nothing. */
const CartLine: FC<Props> = memo(({ line, appeared, onEdit }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('epos-cart-line');
  const cartActions = useExpressCartActions();
  const productLibrary = useExpressProductLibrary();

  const blockRef = useRef<HTMLDivElement>(null);
  /** Set while the slide-out plays - a second "-" during it must not remove another unit. */
  const leavingRef = useRef(false);
  /** Read once at mount - a re-grouped line keeps its place instead of flying in again. */
  const appearedRef = useRef(appeared);

  const hasChildren = line.children.length > 0;
  const hasDiscount = line.originalAmount > line.amount;

  /** A new line must be seen. */
  useEffect(() => {
    if (appearedRef.current && blockRef.current) {
      scrollIntoList(blockRef.current);
    }
  }, []);

  /** Copies the first parent with its add-ons, so the new unit is the same bundle. */
  const handleInc = useCallback(() => {
    if (leavingRef.current) {
      return;
    }

    logger.info('Cart line incremented', { title: line.title, quantity: line.quantity + 1 });
    cartActions.duplicateItemWithChildren(line.ids[0], 1);
  }, [line, cartActions]);

  /** Removes the last parent with its add-ons. The last unit folds out first, then the cart is told. */
  const handleDec = useCallback(() => {
    if (leavingRef.current) {
      return;
    }

    const lastId = line.ids[line.ids.length - 1];
    logger.info('Cart line decremented', { title: line.title, quantity: line.quantity - 1 });

    if (line.quantity > 1) {
      cartActions.removeItemWithChildren(lastId);
      return;
    }

    leavingRef.current = true;
    if (blockRef.current) {
      foldOut(blockRef.current);
    }

    window.setTimeout(() => cartActions.removeItemWithChildren(lastId), LINE_OUT_MS);
  }, [line, cartActions]);

  /** Reopens ItemDetail for this line's item, prefilled. */
  const handleEdit = useCallback(() => {
    const menuItem = productLibrary.menuItemMap[line.itemId];
    if (!menuItem) {
      logger.error('Cannot edit cart line - item no longer in the menu', { itemId: line.itemId });
      return;
    }

    logger.info('Editing cart line', { title: line.title, itemId: line.itemId });
    onEdit(toEdit(line, menuItem));
  }, [line, productLibrary.menuItemMap, onEdit]);

  const {
    touching: editTouching,
    disabled: editD,
    disabledToast: editT,
    ...editButton
  } = useButton({ onClick: handleEdit });

  const blockClassName =
    [appearedRef.current && 'is-new', hasDiscount && 'has-discount'].filter(Boolean).join(' ') ||
    undefined;

  return (
    <Block
      ref={blockRef}
      className={blockClassName}
      data-testid="epos-cart-line"
      data-line-key={line.key}>
      <Row>
        <ItemImage
          imageUrl={line.imageUrl}
          cdnWidth={THUMB_WIDTH * 2}
          width={`${THUMB_WIDTH}px`}
          borderRadius={10}
        />

        <RowText>
          <RowTitle>{line.title}</RowTitle>
          {!!line.variationNames && <RowDescription>{line.variationNames}</RowDescription>}
          {!!line.comment && (
            <RowDescription data-testid="epos-line-comment">
              &ldquo;{line.comment}&rdquo;
            </RowDescription>
          )}
          {line.ageRestricted && (
            <AgeNote data-testid="epos-line-age-restricted">
              {i18n('Express.Cart.AgeRestricted')}
            </AgeNote>
          )}
        </RowText>

        <RowPrice>
          {hasDiscount && <OriginalPrice>{currency.format(line.originalAmount)}</OriginalPrice>}
          <PaidPrice>{currency.format(line.amount)}</PaidPrice>
          {hasDiscount && (
            <DiscountName data-testid="epos-line-discount">{line.discountTitle}</DiscountName>
          )}
        </RowPrice>
      </Row>

      <Below>
        {hasChildren && (
          <Children>
            {line.children.map((child) => (
              <CartChildLine key={child.itemId} child={child} />
            ))}
          </Children>
        )}

        <StepperRow>
          <EditBadge
            {...editButton}
            type="button"
            data-testid="epos-line-edit"
            className={editTouching ? 'is-touching' : undefined}>
            <Icon src={editIcon} color="var(--eposText)" width="14px" height="14px" />
            {i18n('Express.Cart.Edit')}
          </EditBadge>

          <QuantityStepper
            size="small"
            quantity={line.quantity}
            onIncrement={handleInc}
            onDecrement={handleDec}
          />
        </StepperRow>
      </Below>
    </Block>
  );
});

CartLine.displayName = 'CartLine';

export default CartLine;
